import React from "react";
import {
  Image,
  TouchableOpacity,
  type StyleProp,
  TextStyle,
  type ImageSourcePropType,
  type ViewStyle,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { LucideIcon } from "lucide-react-native";

import { useMedicalHistoryTheme } from "../theme/MedicalHistoryTheme";
import { METALLIC_PROGRESS_COLORS } from "./medicalHistoryVisualTokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { cardSurface } from "@louise/moon-ui";

export type SquareOptionButtonProps = {
  icon?: LucideIcon;
  /** Optional raster illustration for object-based answer choices. */
  illustration?: ImageSourcePropType;
  /** Optional emoji for lightweight object-based answer choices. */
  emoji?: string;
  emojiSize?: number;
  label: string;
  isSelected: boolean;
  onPress: () => void;
  iconSize?: number;
  containerSize?: number;
  illustrationSize?: number;
  labelColor?: string;
  labelSize?: "sm" | "lg"; // Small (14px) or Large (18px)
  style?: StyleProp<ViewStyle>;
  /** Stable analytics identifier for the question this option answers. */
  action?: string;
};

/**
 * Square option button component — soft glass style matching onboarding cards.
 * Used for Yes/No options and multi-select options in 2-column grids.
 */
export function SquareOptionButton({
  icon: Icon,
  illustration,
  emoji,
  emojiSize = 24,
  label,
  isSelected,
  onPress,
  iconSize = 20,
  illustrationSize = 88,
  labelColor,
  labelSize = "lg",
  style,
  action,
}: SquareOptionButtonProps) {
  const capturePress = usePressCapture("SquareOptionButton");
  const theme = useMedicalHistoryTheme();
  const hasIllustration = illustration != null;

  // Ensure label is always a string
  const labelText = typeof label === "string" ? label : String(label || "");

  // Build text style array
  const textStyles: TextStyle[] = [
    {
      fontSize: labelSize === "lg" ? 15 : 14,
      fontWeight: "500",
      color: labelColor ?? theme.text,
      textAlign: "center",
      lineHeight: labelSize === "lg" ? 20 : 19,
    },
  ];

  const optionCard = (
    <TouchableOpacity
      onPress={() => {
        capturePress(pressLabel(labelText), action);
        onPress();
      }}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      aria-pressed={isSelected}
      style={[
        {
          ...cardSurface(),
          flex: 1,
          minHeight: 140,
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 20,
        },
        style,
        hasIllustration && {
          minHeight: 168,
          paddingVertical: 16,
        },
        isSelected && {
          borderWidth: 0,
          borderRadius: 14,
          backgroundColor: theme.optionSelectedSurface,
        },
      ]}
    >
      {emoji ? (
        <Text style={{ fontSize: emojiSize }}>{emoji}</Text>
      ) : illustration ? (
        <Image
          source={illustration}
          resizeMode="contain"
          style={{
            height: illustrationSize,
            width: illustrationSize,
          }}
        />
      ) : Icon ? (
        <Icon
          size={iconSize}
          color={isSelected ? theme.text : theme.textFaint}
          strokeWidth={isSelected ? 2 : 1.5}
        />
      ) : null}
      <Text className="font-dm-sans-medium" style={textStyles}>
        {labelText}
      </Text>
    </TouchableOpacity>
  );

  return isSelected ? (
    <LinearGradient
      colors={METALLIC_PROGRESS_COLORS}
      locations={[0, 0.24, 0.5, 0.74, 1]}
      start={{ x: 0, y: 0.5 }}
      end={{ x: 1, y: 0.5 }}
      style={{ flex: 1, borderRadius: 16, padding: 2 }}
    >
      {optionCard}
    </LinearGradient>
  ) : (
    optionCard
  );
}
